import { useEffect, useState } from "react";
import type { Team } from "@sportsos/engine";
import { api } from "../api.ts";
import { connectionMessage, isUnreachable, offlineNotice } from "./connection.ts";
import { useOnline } from "./useOnline.ts";
import { BottomSheet, Chevron } from "./BottomSheet.tsx";
import { avatarColor, initials } from "./shared.ts";
import { TeamDetail } from "./TeamDetail.tsx";

export function MyTeams() {
  const [teams, setTeams] = useState<readonly Team[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const onCreated = (team: Team): void => {
    setTeams((current) => [...(current ?? []), team]);
    setCreating(false);
    setSelectedId(team.id);
  };

  useEffect(() => {
    api.listTeams()
      .then((res) => {
        setTeams(res.teams);
        if (res.teams.length === 1) {
          setSelectedId(res.teams[0]!.id);
        }
      })
      .catch(() => setTeams([]))
      .finally(() => setLoading(false));
  }, []);

  if (selectedId) {
    return (
      <TeamDetail
        teamId={selectedId}
        // Back is always offered now, including for a single team. It used to be withheld
        // there on the grounds that a one-row list is not worth returning to — but the list
        // is also where "Create team" lives, and a scorer with exactly one team is auto-sent
        // into the detail screen, so withholding it left the only way to make a second team
        // unreachable.
        onBack={() => setSelectedId(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="app-page">
        <header className="ds-appbar">
          <span className="ds-appbar__body">
            <b className="ds-appbar__title" style={{ fontSize: "var(--ds-text-xl)" }}>My teams</b>
          </span>
        </header>
        <div style={{ padding: "var(--ds-space-12)", textAlign: "center" }}>
          <span className="ds-caption">Loading teams...</span>
        </div>
      </div>
    );
  }

  if (!teams || teams.length === 0) {
    return (
      <div className="app-page">
        <header className="ds-appbar">
          <span className="ds-appbar__body">
            <b className="ds-appbar__title" style={{ fontSize: "var(--ds-text-xl)" }}>My teams</b>
          </span>
        </header>

        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: "var(--ds-space-16) var(--ds-space-6)",
          textAlign: "center",
        }}>
          <span
            className="ds-avatar ds-avatar--lg ds-avatar--amber"
            style={{ width: 64, height: 64, fontSize: "var(--ds-text-xl)", opacity: 0.5 }}
          >
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9.2" cy="8" r="3.3" />
              <path d="M3 20a6.2 6.2 0 0 1 12.4 0" />
              <path d="M16 5.2a3.3 3.3 0 0 1 0 5.6M17.6 20a6.3 6.3 0 0 0-1.9-4.4" />
            </svg>
          </span>
          <h2 className="ds-heading" style={{ marginTop: "var(--ds-space-5)" }}>No teams yet</h2>
          <p className="ds-caption" style={{ marginTop: "var(--ds-space-2)", maxWidth: 280 }}>
            Create a team here, when you set up your next match, or join one via an invite link.
          </p>
          <button
            className="ds-btn ds-btn--primary ds-btn--lg"
            style={{ marginTop: "var(--ds-space-5)" }}
            onClick={() => setCreating(true)}
          >
            Create team
          </button>
        </div>
        {creating && (
          <CreateTeamSheet onCreated={onCreated} onClose={() => setCreating(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="app-page">
      <header className="ds-appbar">
        <span className="ds-appbar__body">
          <b className="ds-appbar__title" style={{ fontSize: "var(--ds-text-xl)" }}>My teams</b>
          <span className="ds-appbar__meta">{teams.length} team{teams.length !== 1 ? "s" : ""}</span>
        </span>
        <button className="ds-btn ds-btn--secondary" onClick={() => setCreating(true)}>
          Create team
        </button>
      </header>

      <div className="ds-list">
        {teams.map((team) => {
          const color = avatarColor(team.name);
          return (
            <button
              className="ds-list-item"
              key={team.id}
              onClick={() => setSelectedId(team.id)}
            >
              <span className={`ds-avatar ds-avatar--${color}`}>{initials(team.name)}</span>
              <span className="ds-list-item__body">
                <b className="ds-list-item__title">{team.name}</b>
                <span className="ds-list-item__meta">
                  {team.squad.length} player{team.squad.length !== 1 ? "s" : ""}
                  {!team.isOwner && " · you play here"}
                </span>
              </span>
              {Chevron}
            </button>
          );
        })}
      </div>

      {creating && (
        <CreateTeamSheet onCreated={onCreated} onClose={() => setCreating(false)} />
      )}
    </div>
  );
}

/**
 * Create a team from the Teams tab.
 *
 * Exists because team creation used to be a side effect of setting up a match, which is a
 * scorer's route into the product and not a player's — a club captain who wanted a team he
 * was *in* had no way to make one.
 *
 * The toggle is asked rather than inferred. One account is both personas: a neutral scorer
 * recording two clubs plays for neither, a captain setting up his own game plays for one,
 * and nothing about where the team is created tells them apart. Default on, because someone
 * who came to this tab to make a team is usually in it — the scorer's path is match setup,
 * which passes nothing and so stays the old behaviour.
 */
function CreateTeamSheet({
  onCreated,
  onClose,
}: {
  onCreated: (team: Team) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [imPlaying, setImPlaying] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The team is made by the server, like a match, so with no connection the sheet says so as it
  // opens and holds the button back. A request that fails anyway is still explained below.
  const online = useOnline();

  const save = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) { setError("Team name is required"); return; }
    setSaving(true);
    setError(null);
    try {
      onCreated(await api.createTeam({ name: trimmed, playerIds: [], imPlaying }));
    } catch (failure: unknown) {
      setError(
        isUnreachable(failure)
          ? connectionMessage("Creating a team")
          : "Could not create the team. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <BottomSheet title="Create team" onClose={onClose}>
      {!online && (
        <p className="ds-hint ds-hint--error" role="status" style={{ marginBottom: "var(--ds-space-3)" }}>
          {offlineNotice("Creating a team")}
        </p>
      )}
      <div className="ds-field">
        <label className="ds-label" htmlFor="new-team-name">Team name</label>
        <input
          className="ds-input"
          id="new-team-name"
          type="text"
          placeholder="e.g. Sunday Tigers"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="off"
          autoFocus
        />
      </div>

      <label
        className="row"
        style={{ marginTop: "var(--ds-space-4)", gap: "var(--ds-space-3)", alignItems: "center" }}
      >
        <input
          type="checkbox"
          checked={imPlaying}
          onChange={(e) => setImPlaying(e.target.checked)}
        />
        <span>
          <b style={{ display: "block" }}>I play for this team</b>
          <span className="ds-caption">
            Adds you to the squad as captain. Leave off if you only score for them.
          </span>
        </span>
      </label>

      {error && (
        <p className="ds-hint ds-hint--error" style={{ marginTop: "var(--ds-space-2)" }}>{error}</p>
      )}

      <button
        className="ds-btn ds-btn--primary ds-btn--lg ds-btn--block"
        style={{ marginTop: "var(--ds-space-5)" }}
        disabled={saving || !online || !name.trim()}
        onClick={() => { void save(); }}
      >
        {saving ? "Creating..." : "Create team"}
      </button>
    </BottomSheet>
  );
}
